// axiosConfig.js
import axios from 'axios';

// Function to get the base URL - lazily evaluated
const getBaseURL = () => {
  return window.runtimeConfig?.BASE_URL || 'https://localhost:7179'; // fallback
};

// Export BASE_URL as a getter function
export const BASE_URL = getBaseURL();

const axiosInstance = axios.create({
  baseURL: getBaseURL(), // Use the function instead of direct access
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Track when a token refresh is in progress to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshSubscribers = [];

// Function to add callbacks to the queue
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Function to execute all callbacks when token refresh is complete
const onTokenRefreshed = () => {
  refreshSubscribers.forEach(callback => callback());
  refreshSubscribers = [];
};

axiosInstance.interceptors.response.use(
  response => {
    // Update last activity timestamp on successful responses
    if (localStorage.getItem('isAuthenticated') === 'true') {
      localStorage.setItem('lastActivity', Date.now().toString());
    }
    return response;
  },
  async error => {
    const originalRequest = error.config;
    
    // Handle 401 errors and token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If a refresh is already in progress, wait for it to complete
        return new Promise((resolve) => {
          subscribeTokenRefresh(() => {
            // Don't modify the headers - cookies handle authentication
            resolve(axiosInstance(originalRequest));
          });
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Attempt to refresh the token - use getBaseURL() function
        const response = await axios.post(
          `${getBaseURL()}/Account/refresh-token`, 
          {}, 
          { withCredentials: true }
        );
        
        if (response.data.result === true) {
          // Token refresh successful - update authentication state
          localStorage.setItem('lastActivity', Date.now().toString());
          
          // Notify all waiting requests that the token has been refreshed
          onTokenRefreshed();
          
          // Retry the original request - cookies will be sent automatically
          isRefreshing = false;
          return axiosInstance(originalRequest);
        } else {
          // Check if this is a temporary or permanent failure
          const errorMessage = response.data.errorResponse?.message || '';
          console.log('Token refresh response:', errorMessage);
          
          if (errorMessage.includes("Invalid token") || 
              errorMessage.includes("inactive for too long") ||
              errorMessage.includes("missing") ||
              errorMessage.includes("Invalid request context")) {
            // Permanent failure - clear and redirect
            isRefreshing = false;
            localStorage.removeItem('roleId');
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('lastActivity');
            window.location.href = '/auth/login';
            return Promise.reject(new Error(errorMessage || 'Authentication failed'));
          } else {
            // Possibly temporary failure - just reject but don't redirect
            isRefreshing = false;
            return Promise.reject(new Error(errorMessage || 'Token refresh failed'));
          }
        }
      } catch (refreshError) {
        // Check if it's a network error (which might be temporary)
        isRefreshing = false;
        if (!refreshError.response) {
          // Network error - could be temporary, just reject
          console.error('Network error during token refresh:', refreshError);
          return Promise.reject(refreshError);
        }
        
        // Server returned an error response - likely permanent
        console.error('Server error during token refresh:', refreshError);
        localStorage.removeItem('roleId');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('lastActivity');
        window.location.href = '/auth/login';
        return Promise.reject(refreshError);
      }
    }
    
    // For other error statuses like 403 (forbidden)
    if (error.response?.status === 403) {
      // Update lastActivity since user is making requests, even if they're forbidden
      if (localStorage.getItem('isAuthenticated') === 'true') {
        localStorage.setItem('lastActivity', Date.now().toString());
      }
    }
    
    // Handle other errors
    return Promise.reject(error);
  }
);

export default axiosInstance;
